import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";
import { IMG } from "@/lib/assets";
import { TESTIDS } from "@/constants/testIds";
import Reveal from "@/components/ui-brand/Reveal";
import PeonyMark from "@/components/ui-brand/PeonyMark";

const ProjectsPage = () => {
  const { lang } = useLanguage();

  const tiles = [
    {
      key: "reviews",
      title: { de: "Online-Bewertungsmanagement", en: "Online review management" },
      body: {
        de: "Strukturiert, dokumentiert, nachvollziehbar — für Google & Trustpilot.",
        en: "Structured, documented, traceable — for Google & Trustpilot.",
      },
    },
    {
      key: "clearsignal",
      title: { de: "CLEAR SIGNAL™", en: "CLEAR SIGNAL™" },
      body: {
        de: "Profilanalysen, die zeigen wie du wirklich wahrgenommen wirst.",
        en: "Profile analyses that show how you are actually perceived.",
      },
    },
    {
      key: "social",
      title: { de: "Social Media & DSA", en: "Social media & DSA" },
      body: {
        de: "Begleitung bei gesperrten Accounts — diskret, strukturiert, dokumentiert.",
        en: "Guidance for suspended accounts — discreet, structured, documented.",
      },
    },
    {
      key: "websites",
      title: { de: "Webseiten & Hosting", en: "Websites & hosting" },
      body: {
        de: "Von der Idee bis zum Launch — mit klarer Struktur und starker Wirkung.",
        en: "From concept to launch — with clear structure and strong impact.",
      },
    },
  ];

  return (
    <>
      {/* HERO */}
      <section
        data-testid={TESTIDS.projectsHero}
        className="relative px-6 md:px-12 lg:px-24 pt-6 md:pt-12 pb-20 md:pb-28 overflow-hidden"
      >
        <PeonyMark size={680} opacity={0.05} className="absolute -right-32 -top-20 pointer-events-none" />
        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <p className="eyebrow">{pick(t.projects.eyebrow, lang)}</p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="headline-hero mt-10">
                <span className="italic-serif text-atmosphere">
                  {pick(t.projects.title, lang).split(" ")[0]}
                </span>{" "}
                {pick(t.projects.title, lang).split(" ").slice(1).join(" ")}
              </h1>
            </Reveal>
            <Reveal delay={280}>
              <p className="body-lux-lg max-w-2xl mt-12">{pick(t.projects.lede, lang)}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SGB FEATURE BLOCK — moved from Home, 1:1 */}
      <section className="relative px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <Reveal>
          <Link
            to="/sgb"
            data-testid="projects-sgb-block"
            className="group block relative overflow-hidden aspect-[16/11] md:aspect-[16/9] bg-depth"
          >
            <img
              src={IMG.sgbAndroid}
              alt="Sichtbar Gut Bewertet — Android Key Visual"
              className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[1500ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-depth via-depth/40 to-transparent" />
            <div className="absolute left-8 bottom-8 right-8 md:left-12 md:bottom-12 md:right-12 text-canvas">
              <span className="eyebrow-light">{pick(t.common.active, lang)} · 01</span>
              <h3 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
                Sichtbar Gut Bewertet
              </h3>
              <p className="mt-4 font-light text-warmth/90 text-sm md:text-base max-w-md">
                {lang === "de"
                  ? "Bewertungsmanagement & digitale Reputation — als eigenständige Marke unter patrice.studio."
                  : "Reputation & review management — as its own brand under patrice.studio."}
              </p>
              <span className="mt-8 inline-block font-mono text-[0.7rem] tracking-[0.32em] uppercase text-warmth group-hover:text-canvas transition-colors">
                {pick({ de: "Marke besuchen", en: "Visit brand" }, lang)} ↗
              </span>
            </div>
          </Link>
        </Reveal>
      </section>

      {/* FOUR TILES — direct leistung breakdown */}
      <section className="relative px-6 md:px-12 lg:px-24 py-12 md:py-20">
        <Reveal>
          <p className="eyebrow">— {lang === "de" ? "Was darin lebt" : "What lives inside"}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-12 gap-4 md:gap-6">
          {tiles.map((tile, i) => (
            <Reveal key={tile.key} delay={i * 80} className="col-span-12 md:col-span-6">
              <Link
                to="/sgb"
                data-testid={`projects-tile-${tile.key}`}
                className="group block bg-clarity p-8 md:p-10 border border-warmth/40 hover:border-depth transition-colors duration-500 h-full"
              >
                <p className="font-mono text-[0.6rem] tracking-[0.32em] uppercase text-atmosphere">
                  0{i + 1}
                </p>
                <h3 className="font-display text-2xl md:text-3xl mt-4 text-depth leading-tight">
                  {pick(tile.title, lang)}
                </h3>
                <p className="body-lux mt-6 max-w-md">{pick(tile.body, lang)}</p>
                <span className="link-underline mt-8 inline-block font-mono text-[0.7rem] tracking-[0.32em] uppercase text-depth">
                  {pick(t.common.readMore, lang)} ↗
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLOSING */}
      <section className="relative bg-depth text-canvas px-6 md:px-12 lg:px-24 py-24 md:py-32 overflow-hidden">
        <PeonyMark size={700} color="#FAF7F2" opacity={0.07} className="absolute -left-32 -bottom-32" />
        <div className="relative grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-9">
            <Reveal>
              <p className="eyebrow-light">— {lang === "de" ? "Universum" : "Universe"}</p>
              <h2 className="font-display italic text-[clamp(2.4rem,5vw,4.6rem)] leading-[1.05] mt-8 max-w-4xl">
                {lang === "de"
                  ? "Mehr lebt in den Erlebnisräumen."
                  : "More lives inside the inner rooms."}
              </h2>
              <p className="mt-10 max-w-2xl font-light text-warmth/90 text-lg">
                {lang === "de"
                  ? "Mehr wartet in den Erlebnisräumen. Fotografie, Kunst, Musik, Atelier & Kreatives — fünf Räume, die du im Home-Studio betreten kannst."
                  : "More awaits in the inner rooms. Photography, art, music, atelier & creative — five rooms you can enter from the home studio."}
              </p>
              <Link to="/" className="btn-light mt-12 w-fit">
                {lang === "de" ? "Zu den Erlebnisräumen" : "To the inner rooms"} <span aria-hidden>↗</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProjectsPage;
